import {
  TxGrpcApi,
  createTransactionWithSigners,
  IndexerGrpcTransactionApi,
  getGasPriceBasedOnMessage,
  CreateTransactionWithSignersArgs,
  Msgs,
  ChainRestAuthApi,
  MsgAuthzExec,
  TxResponse,
  TxRestApi,
} from '@ninjablaze/sdk-ts'
import { getStdFee, BigNumberInBase } from '@injectivelabs/utils'
import { GeneralException } from '@injectivelabs/exceptions'
import { getNetworkInfo, getNetworkEndpoints, NetworkEndpoints, Network } from '@injectivelabs/networks'
import { ChainId, EthereumChainId } from '@injectivelabs/ts-types'
import { FEE_PAYER, FEE_PAYER_ACCOUNT, GAS_PRICE } from '@/constants/constants'
import { TransactionException, GrpcUnaryRequestException } from '@injectivelabs/exceptions'
import { PublicKey, PrivateKey } from '@ninjablaze/sdk-ts'
import { delay } from '@/helpers/delay'

/**
 * This class is used to broadcast transactions
 * using the WalletStrategy as a handler
 * for the sign/broadcast flow of the transactions
 *
 * Mainly used for building UI products
 */

interface MsgBroadcasterTxOptions {
  msgs: Msgs | Msgs[]
  injectiveAddress: string
  ethereumAddress?: string
  memo?: string
  timeoutHeight?: number
  gas?: {
    gasPrice?: string
    gas?: number /** gas limit */
    feePayer?: string
    granter?: string
  }
}

interface MsgBroadcasterOptionsWithPk {
  network: Network

  /**
   * Only used if we want to override the default
   * endpoints taken from the network param
   */
  endpoints?: {
    indexer: string
    grpc: string
    rest: string
  }
  privateKey: string | PrivateKey /* hex or PrivateKey class */
  ethereumChainId?: EthereumChainId
  simulateTx?: boolean

  gasMultiplier?: number
}
let isBusy = false
let feePayerBaseAccount: any = FEE_PAYER_ACCOUNT




export class MsgCustomBroadcaster {

  public endpoints: NetworkEndpoints

  public chainId: ChainId

  public ethereumChainId?: EthereumChainId

  public privateKey: PrivateKey

  public simulateTx = false

  public gasMultiplier = 1.1

  constructor(options: MsgBroadcasterOptionsWithPk) {
    const networkInfo = getNetworkInfo(options.network)
    const endpoints = getNetworkEndpoints(options.network)

    this.gasMultiplier = (options.gasMultiplier || this.gasMultiplier) * 1.6
    this.simulateTx = options.simulateTx || false
    this.chainId = networkInfo.chainId
    this.ethereumChainId = options.ethereumChainId || networkInfo.ethereumChainId
    this.endpoints = { ...endpoints, ...(options.endpoints || {}) }
    this.privateKey =
      options.privateKey instanceof PrivateKey ? options.privateKey : PrivateKey.fromHex(options.privateKey)
  }

  private getGasPriceBasedOnMessagev2 = (msgs: Msgs[]) => {
    if (msgs[0] instanceof MsgAuthzExec) {
      const msg = msgs[0] as MsgAuthzExec
      const execMsgs = Array.isArray(msg.params.msgs) ? msg.params.msgs : [msg.params.msgs]
      return getGasPriceBasedOnMessage(execMsgs) / Math.floor((execMsgs.length + 1) / 2) / 1.5
    }

    return getGasPriceBasedOnMessage(msgs)
  }

  async broadcastWithFeeDelegation(transaction: MsgBroadcasterTxOptions) {
    while (isBusy) {
      await new Promise((resolve) => setTimeout(resolve, 1000))
    }

    isBusy = true

    try {
      return await this.broadcastCosmosWithFeeDelegation({
        ...transaction,
        ethereumAddress: transaction.injectiveAddress,
      })
    } catch (error) {
      throw error
    } finally {
      isBusy = false
    }
  }

  /**
   * Prepare/sign/broadcast transaction using
   * Cosmos native wallets using the Web3Gateway.
   *
   * @param tx The transaction that needs to be broadcasted
   * @returns transaction hash
   */
  private async broadcastCosmosWithFeeDelegation(tx: MsgBroadcasterTxOptions): Promise<TxResponse> {
    const { chainId, endpoints } = this
    // const { walletStrategy } = options
    const msgs = Array.isArray(tx.msgs) ? tx.msgs : [tx.msgs]

    const feePayerPubKey = await this.fetchFeePayerPubKey(FEE_PAYER)
    const feePayerPublicKey = PublicKey.fromBase64(feePayerPubKey)

    const feePayer = feePayerPublicKey.toAddress().address
    /** Account Details * */

    const chainGrpcAuthApi = new ChainRestAuthApi(endpoints.rest)

    let localStorageAccount = JSON.parse(localStorage.getItem('sequence') || '{}')
    let accountDetails: { base_account: { account_number: string; sequence: string } } | undefined = undefined
    if (localStorageAccount.injectiveAddress == tx.injectiveAddress) {
      accountDetails = {
        base_account: {
          account_number: localStorageAccount.accountNumber,
          sequence: localStorageAccount.sequence,
        },
      }
    }
    accountDetails ??= await chainGrpcAuthApi.fetchAccount(tx.injectiveAddress).then(x => x.account)

    // const accountDetails = baseAccount.toAccountDetails()

    /** Fee Payer Account Details */
    if (!feePayerBaseAccount) {
      feePayerBaseAccount = await chainGrpcAuthApi.fetchAccount(feePayer)
    }

    /** Block Details */
    // const chainRestTendermintApi = new Cha(endpoints.rest)
    // const latestBlock = await chainRestTendermintApi.fetchLatestBlock()
    // const latestHeight = latestBlock.header.height
    // const timeoutHeight = new BigNumberInBase(latestHeight).plus(
    //   DEFAULT_BLOCK_TIMEOUT_HEIGHT,
    // )

    const pubKey = await this.privateKey.toPublicKey().toBase64()
    const gas = (tx.gas?.gas || this.getGasPriceBasedOnMessagev2(msgs) * this.gasMultiplier).toString()

    /** Prepare the Transaction * */
    const { txRaw, signHashedBytes } = await this.getTxWithSignersAndStdFee({
      chainId,
      memo: tx.memo,
      message: msgs,
      timeoutHeight: tx.timeoutHeight ? Number(tx.timeoutHeight) : undefined,
      signers: [
        {
          pubKey,
          accountNumber: Number(accountDetails!.base_account.account_number),
          sequence: Number(accountDetails!.base_account.sequence),
        },
        {
          pubKey: feePayerPublicKey.toBase64(),
          accountNumber: Number(feePayerBaseAccount?.baseAccount?.accountNumber || feePayerBaseAccount?.account?.base_account?.accountNumber),
          sequence: Number(feePayerBaseAccount?.baseAccount?.sequence || feePayerBaseAccount?.account?.base_account?.sequence),
        },
      ],
      fee: getStdFee({ ...tx.gas, gas, payer: feePayer, gasPrice: GAS_PRICE }),
    })

    // const directSignResponse = (await walletStrategy.signCosmosTransaction({
    //   txRaw,
    //   chainId,
    //   address: tx.injectivexAddress,
    //   accountNumber: accountDetails.accountNumber,
    // })) as DirectSignResponse

    const directSignResponse = await this.privateKey.signHashedEcda(Buffer.from(signHashedBytes))

    const transactionApi = new IndexerGrpcTransactionApi(endpoints.web3gw || endpoints.indexer)

    const timeout = 60000




    try {
      for (let i = 0; i < 10; i++) {
        const promiseResult = new Promise((resolve) => {
          window.addEventListener('transaction', (t: any) => {
            const responseResult = t.detail
            const signatures = responseResult.events.filter((x: any) => x.type == 'tx').flatMap((x: any) => x.attributes).filter((x: any) => x.key == "signature").flatMap((x: any) => x.value) as string[]

            if (!signatures.includes(Buffer.from(directSignResponse).toString('base64'))) {
              return;
            }
            resolve(
              {
                ...responseResult,
                txHash: responseResult.id,
                code: responseResult.status,

              });
          }, { once: true });
        });
        const response = await transactionApi.broadcastCosmosTxRequest({
          address: tx.injectiveAddress,
          txRaw: txRaw,
          signature: Buffer.from(directSignResponse).toString('base64'),
          pubKey: {
            type: this.privateKey.toPublicKey().toAny().typeUrl,
            value: this.privateKey.toPublicKey().toBase64(),
          },
        })

        const txResponse = response!
        if (txResponse.code !== 0) {
          if (txResponse.codespace == 'sdk' && txResponse.code == 32) {
            const seq = txResponse.rawLog.match(/expected (\d+)/)
            localStorage.setItem(
              'sequence',
              JSON.stringify({
                injectiveAddress: tx.injectiveAddress,
                accountNumber: accountDetails!.base_account.account_number,
                sequence: seq?.at(1)!,
              })
            )
            return await this.broadcastCosmosWithFeeDelegation(tx)
          }

          throw new TransactionException(new Error(txResponse.rawLog), {
            contextCode: txResponse.code,
            contextModule: txResponse.codespace,
          })
        }

        if (txResponse.code !== 0) {
          throw new TransactionException(new Error(txResponse.rawLog), {
            contextCode: txResponse.code,
            contextModule: txResponse.codespace,
          })
        }

        localStorage.setItem(
          'sequence',
          JSON.stringify({
            injectiveAddress: tx.injectiveAddress,
            accountNumber: accountDetails!.base_account.account_number,
            sequence: Number(accountDetails!.base_account.sequence) + 1,
          })
        )

        try {
          const abortController = new AbortController()
          const fetchFunc = async () => {
            await delay(1000)
            const result = await this.fetchTxPoll(endpoints, txResponse.txHash, timeout / 10, abortController.signal)
            return result
          }

          const result = await Promise.race([promiseResult as any, fetchFunc()])
          abortController.abort()

          return result;

        } catch (error) {
          if (error instanceof GrpcUnaryRequestException) {
            if (error.message.includes('Transaction was not included in a block before timeout')) {
              continue
            }
          }
          throw error
        }
      }
      throw new GrpcUnaryRequestException(
        new Error(`Transaction was not included in a block before timeout of ${timeout}ms`),
        {
          context: 'TxGrpcApi',
          contextModule: 'fetch-tx-poll',
        }
      )
    } catch (e: any) {
      if (e instanceof TransactionException) {
        console.log(e)
        throw e
      }
      if (!e.originalMessage.includes('account sequence mismatch, expected')) {
        throw new TransactionException(new Error((e as any).message))
      }
      const seq = e.originalMessage.match(/expected (\d+)/)
      localStorage.setItem(
        'sequence',
        JSON.stringify({
          injectiveAddress: tx.injectiveAddress,
          accountNumber: accountDetails!.base_account.account_number,
          sequence: seq?.at(1)!,
        })
      )
      return await this.broadcastCosmosWithFeeDelegation(tx)

    }
  }

  async fetchTxPoll(endpoints: any, txHash: string, timeout = 60000, signal?: AbortSignal): Promise<TxResponse> {
    const POLL_INTERVAL = 100
    for (let i = 0; i <= timeout / POLL_INTERVAL; i += 1) {
      try {
        if (signal?.aborted) {
          throw new GrpcUnaryRequestException(
            new Error('Transaction fetch aborted'),
            {
              context: 'TxGrpcApi',
              contextModule: 'fetch-tx-poll',
            }
          )
        }
        const txResponse = await new TxRestApi(endpoints.rest).fetchTx(txHash)
        if (txResponse) {
          return txResponse
        }
      } catch (e: any) {
        // We throw only if the transaction failed on chain

        if (e instanceof TransactionException || e.type == 'chain-error') {
          throw e
        }
      }
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL))
    }
    // Transaction was not included in the block in the desired timeout
    throw new GrpcUnaryRequestException(
      new Error(`Transaction was not included in a block before timeout of ${timeout}ms`),
      {
        context: 'TxGrpcApi',
        contextModule: 'fetch-tx-poll',
      }
    )
  }

  /**
   * Fetch the fee payer's pub key from the web3 gateway
   *
   * Returns a base64 version of it
   */
  private async fetchFeePayerPubKey(existingFeePayerPubKey?: string) {
    if (existingFeePayerPubKey) {
      return existingFeePayerPubKey
    }

    const { endpoints } = this

    const transactionApi = new IndexerGrpcTransactionApi(endpoints.indexer)
    const response = await transactionApi.fetchFeePayer()

    if (!response.feePayerPubKey) {
      throw new GeneralException(new Error('Please provide a feePayerPubKey'))
    }

    if (response.feePayerPubKey.key.startsWith('0x') || response.feePayerPubKey.key.length === 66) {
      return Buffer.from(response.feePayerPubKey.key, 'hex').toString('base64')
    }

    return response.feePayerPubKey.key
  }

  /**
   * In case we don't want to simulate the transaction
   * we get the gas limit based on the message type.
   *
   * If we want to simulate the transaction we set the
   * gas limit based on the simulation and add a small multiplier
   * to be safe (factor of 1.2)
   */
  private async getTxWithSignersAndStdFee(args: CreateTransactionWithSignersArgs) {
    const { simulateTx } = this

    if (!simulateTx) {
      return {
        ...createTransactionWithSigners(args),
        stdFee: getStdFee(args.fee),
      }
    }

    const result = await this.simulateTxWithSigners(args)

    if (!result.gasInfo?.gasUsed) {
      return {
        ...createTransactionWithSigners(args),
        stdFee: getStdFee(args.fee),
      }
    }

    const stdGasFee = {
      ...getStdFee({
        ...args.fee as any,
        gas: new BigNumberInBase(result.gasInfo.gasUsed).times(1.2).toFixed(),
      }),
    }

    return {
      ...createTransactionWithSigners({
        ...args,
        fee: stdGasFee,
      }),
      stdFee: stdGasFee,
    }
  }

  /**
   * Create TxRaw and simulate it
   */
  private async simulateTxWithSigners(args: CreateTransactionWithSignersArgs) {
    const { endpoints } = this
    const { txRaw } = createTransactionWithSigners(args)

    txRaw.signatures = Array(Array.isArray(args.signers) ? args.signers.length : 1).fill(new Uint8Array(0))

    const simulationResponse = await new TxRestApi(endpoints.rest).simulate(txRaw)

    return simulationResponse
  }
}
